import React from "react";
import { motion } from "framer-motion";
import { TID } from "../../lib/testIds";

const PRINCIPLES = [
  {
    n: "01",
    title: "Product Thinking",
    desc: "We look beyond the requested feature. We seek to understand the business objective, user workflow, technical constraints, and long-term implications before recommending an engineering path.",
  },
  {
    n: "02",
    title: "Architecture Perspective",
    desc: "Complex systems need deliberate technical decisions. We consider scalability, maintainability, integration complexity, security, infrastructure, and future product evolution.",
  },
  {
    n: "03",
    title: "AI-Native Approach",
    desc: "We treat AI as an engineering capability, not a marketing label. Our focus is on connecting AI with real products, workflows, data, systems, and measurable use cases.",
  },
  {
    n: "04",
    title: "Flexible Collaboration",
    desc: "Engage around the problem you actually need to solve. Start with a focused assessment, a product initiative, a modernization program, or dedicated engineering capacity.",
  },
  {
    n: "05",
    title: "Long-Term Engineering",
    desc: "We build with future change in mind. Products evolve. Customer expectations change. Architecture should support that evolution rather than make every future decision more expensive.",
  },
];

export const WhyTechovarya = () => {
  return (
    <section
      id="why"
      data-testid={TID.whyTechovarya}
      className="relative bg-[#050505] py-24 md:py-32 border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sticky heading */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-[#FF5F1F]" />
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                  Why Techovarya
                </span>
              </div>
              <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
                A Product Engineering Partner. <em className="text-[#FF5F1F] not-italic">Not a Ticket Factory.</em>
              </h2>
              <p className="mt-8 text-sm md:text-base text-zinc-400 leading-relaxed max-w-md">
                Five operating principles shape every engagement — from the first strategy
                conversation to the systems we ship into production.
              </p>
            </div>
          </div>

          {/* Principles list */}
          <div className="lg:col-span-7 flex flex-col gap-4 md:gap-6">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={i}
                data-testid={TID.principleCard(i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="group relative border border-white/10 hover:border-[#FF5F1F]/40 bg-[#0A0A0A] p-6 md:p-8 transition-all"
              >
                <div className="flex items-start gap-6">
                  <div className="shrink-0">
                    <div className="font-headings text-4xl md:text-5xl font-black text-zinc-800 group-hover:text-[#FF5F1F] transition-colors">
                      {p.n}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-headings text-xl md:text-2xl font-bold tracking-tight text-white leading-tight mb-3">
                      {p.title}
                    </h3>
                    <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
